/**
 * Kleine pure helpers voor tweestapsverificatie met een authenticator-app
 * (TOTP), gedeeld door het inlogscherm en het profielblok.
 *
 * AI-CONTEXT: See docs/features/mfa-totp.md
 */

/** De naam waaronder het account in de authenticator-app verschijnt, als het merk onbekend is. */
export const TOTP_ISSUER = 'Deccos'

/**
 * Maakt van wat iemand intypt of plakt een code van zes cijfers.
 *
 * @param {*} input - Zoals getypt, bijv. '123 456'
 * @returns {string} Zes cijfers, of '' als het dat niet is
 */
export const normalizeTotpCode = (input) => {
    if (typeof input !== 'string') return ''
    const code = input.replace(/[\s-]/g, '')
    return /^\d{6}$/.test(code) ? code : ''
}

/**
 * @typedef {object} FactorInfo
 * @property {string} uid - ID van de factor bij Firebase
 * @property {string} factorId - 'totp' of 'phone'
 * @property {string} [displayName] - Naam die bij het koppelen is gegeven
 * @property {string} [enrollmentTime] - Moment van koppelen
 */

/**
 * @param {Array<FactorInfo>|null|undefined} factors
 * @returns {FactorInfo|null} De eerste authenticator-app-factor
 */
const firstTotp = (factors) =>
    (Array.isArray(factors) ? factors : []).find((f) => f?.factorId === 'totp') || null

/** De TOTP-hint uit `resolver.hints` bij het inloggen. */
export const totpHint = firstTotp

/** De gekoppelde TOTP-factor uit `multiFactor(user).enrolledFactors`. */
export const totpFactor = firstTotp

/**
 * Een Firebase-foutcode rond tweestapsverificatie als zin voor het scherm.
 * Zelfde opzet als `loginMessage`: elke code levert iets op, en de onbekende
 * tak noemt de code.
 *
 * @param {string|undefined|null} code - bijv. 'auth/invalid-verification-code'
 * @returns {string} Nooit leeg
 */
export const totpMessage = (code) => {
    switch (code) {
        case 'auth/invalid-verification-code':
        case 'auth/missing-verification-code':
            return 'Deze code klopt niet. Neem de code die nu in je authenticator-app staat; hij verandert elke 30 seconden.'
        case 'auth/invalid-multi-factor-session':
        case 'auth/missing-multi-factor-session':
        case 'auth/totp-challenge-timeout':
            return 'Dit duurde te lang. Log opnieuw in met je e-mailadres en wachtwoord.'
        case 'auth/operation-not-allowed':
            return 'Tweestapsverificatie met een authenticator-app is in Deccos nog niet beschikbaar.'
        case 'auth/unverified-email':
            return 'Bevestig eerst je e-mailadres; daarna kun je een authenticator-app koppelen.'
        case 'auth/requires-recent-login':
            return 'Bevestig eerst je wachtwoord.'
        case 'auth/second-factor-already-in-use':
        case 'auth/maximum-second-factor-count-exceeded':
            return 'Er is al een authenticator-app gekoppeld aan dit account.'
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
            return 'Dit wachtwoord klopt niet.'
        case 'auth/too-many-requests':
            return 'Er zijn te veel pogingen gedaan. Wacht een paar minuten en probeer het opnieuw.'
        case 'auth/network-request-failed':
            return 'Er is geen verbinding met Deccos. Controleer je internetverbinding en probeer het opnieuw.'
        default:
            return `Dat is niet gelukt (${code || 'onbekende fout'}). Probeer het zo nog eens, of stuur een mailtje naar info@deccos.nl.`
    }
}

/**
 * Moet deze gebruiker eerst een authenticator-app koppelen voordat hij verder
 * kan (MFA fase 5)? Ja als de claim `mfa_required` erop staat en hij NIET met
 * een tweede stap is ingelogd — dan weigeren de regels en de Cloud Functions
 * alles, en toont MfaGate het koppelscherm.
 *
 * Tegendeel van functions/utils/mfaSatisfied.js; de pariteitstest staat daar.
 *
 * @param {object|null|undefined} claims - getIdTokenResult().claims
 * @returns {boolean}
 */
export const needsMfaSetup = (claims) =>
    !!claims && claims.mfa_required === true && !claims.firebase?.sign_in_second_factor
